import { generateFirebaseId } from "senf-shared";

/**
 * Adds UTM parameters to a given URL based on the provided platform and sharing context.
 *
 * @param {string} url - The original URL to which UTM parameters will be added.
 * @param {string} platform - The platform to which the link is being shared.
 * @param {string} trackLocation - The context from which the link is being shared.
 * @returns {string} - The updated URL with UTM parameters.
 */
export const addUtmParameters = (
  url: string,
  platform: string,
  trackLocation: string,
  app: "mainApp" | "dashboard"
): string => {
  // Remove any existing UTM parameters from the URL
  const urlWithoutUtm = url.replace(/(\?|&)(utm_[^=]+=[^&]*)/g, "");

  // Clean up any leftover '?' or '&' if UTM parameters were at the end of the URL
  const cleanUrl = urlWithoutUtm.replace(/(\?|&)$/, "");

  if (app === "dashboard") return `${cleanUrl}`;

  // Initialize with platform and sharing context parameters
  let utmParameters = `utm_source=${encodeURIComponent(
    platform
  )}&utm_sharing_context=${encodeURIComponent(trackLocation)}`;

  const urlSegments = cleanUrl.split("/");

  const isSurvey = cleanUrl.includes("/survey/");
  const isProject = cleanUrl.includes("/projects/");

  let id = "";
  utmParameters += `&utm_id=${generateFirebaseId()}`;

  if (isProject) {
    // If it's a project, find the index of 'projects' and use the next segment as the ID
    const projectIndex = urlSegments.indexOf("projects");
    if (projectIndex !== -1 && projectIndex < urlSegments.length - 1) {
      id = urlSegments[projectIndex + 1];
      utmParameters += `&utm_projectId=${encodeURIComponent(id)}`;
    }
  }

  if (isSurvey) {
    // If it's a survey, use the last segment as the ID
    id = urlSegments[urlSegments.length - 1];
    utmParameters += `&utm_surveyId=${encodeURIComponent(id)}`;
  }

  // Determine separator based on existing URL parameters
  const separator = cleanUrl.includes("?") ? "&" : "?";

  // Return the updated URL with UTM parameters
  return `${cleanUrl}${separator}${utmParameters}`;
};

export const openPopup = (path: string) =>
  window.open(
    path,
    undefined,
    "popup=1,scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=800,height=600,left=400,top=300"
  );

export const NavigatorShare = (
  isMobile: boolean,
  path = window.location.href
) => {
  return new Promise((resolve, reject) => {
    if (isMobile && navigator.share) {
      reject();

      navigator
        .share({
          title: `Senf.app`,
          url: path,
        })
        .then(() => {
          console.log("Thanks for sharing!");
        })
        .catch(console.error);
    } else {
      resolve(true);
    }
  });
};
